import styles from './index.module.less';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Button, message } from 'antd';
import { postVideoMonitorApi } from '@/lib/service/contont-monitor';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';

const Index: React.FC<any> = ({ callBackName, loading, setLoading, videoDelete, setVideoDelete, setIsSearching }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const [imageData, setImageData] = useState<string>('');

  useEffect(() => {
    if (videoDelete) {
      setImageData('');
      setVideoDelete(false);
      setUploadStatus(1);
      setImageData('');
      fileInputRef.current!.value = '';
    }
  }, [videoDelete]);

  const getVideoFrame = (file: File) => {
    return new Promise((resolve, reject) => {
      const videoElement = document.createElement('video');
      const url = URL.createObjectURL(file);
      videoElement.src = url;
      videoElement.addEventListener('error', (event) => {
        console.error('Error loading video:', event);
        reject(new Error('Error loading video'));
      });

      videoElement.addEventListener('loadedmetadata', () => {
        videoElement.currentTime = Math.min(0, videoElement.duration);
      });

      videoElement.addEventListener('seeked', () => {
        const timer = setTimeout(() => {
          console.log('Seeked to time:', videoElement.currentTime);
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;
          const context = canvas.getContext('2d');

          if (context) {
            context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
            const imageData = canvas.toDataURL('image/png'); // 获取图像数据
            resolve(imageData); // 返回图像数据
          } else {
            reject(new Error('Canvas context not available'));
          }
          clearTimeout(timer);
        }, 1000);
      });

      videoElement.load(); // 开始加载
    });
  };

  const base64ToBlob = (base64: string, type = 'image/png') => {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Uint8Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    return new Blob([byteNumbers], { type: type });
  };

  const [videoProgress, setVideoProgress] = useState<number>(0);

  const handleClick = () => {
    if (uploadStatus != 1) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleUpload = (file: File) => {
    if (file.size > 100 * 1024 * 1024) {
      message.error(t('File size cannot exceed 100MB'));
      setUploadStatus(1);
      return;
    }
    // if (
    //   file.type !== 'video/mp4' &&
    //   file.type !== 'video/mov' &&
    //   file.type !== 'video/avi' &&
    //   file.type !== 'video/quicktime' &&
    //   file.type !== 'video/x-matroska' &&
    //   file.type !== 'video/x-ms-wmv'
    // ) {
    //   message.error(t('File format error'));
    //   return;
    // }

    // ✅ INP 优化：立即显示 loading 态（< 10ms）
    const startTime = performance.now();
    console.log('✅ File validated, showing upload progress at', startTime);
    setUploadStatus(2);

    // ✅ 使用 requestAnimationFrame 确保 loading 态在下一帧渲染
    requestAnimationFrame(() => {
      const renderTime = performance.now() - startTime;
      console.log(`⏱️ Upload progress rendered in ${renderTime.toFixed(2)}ms`);
    });

    // ✅ 异步处理视频帧提取（不阻塞主线程）
    setTimeout(() => {
      const formData = new FormData();
      formData.append('file', file);

      // 视频帧提取（在后台执行，不阻塞用户交互）
      getVideoFrame(file).then((frame: any) => {
        const blob = base64ToBlob(frame);
        const formData = new FormData();
        formData.append('file', blob, 'image.png');
        axios
          .post(process.env.NEXT_APP_API_HOST + '/account/upload/image', formData, {
            headers: { Authorization: Cookies.get('userToken') },
          })
          .then((res) => {
            setImageData(res.data.data);
          })
          .catch((error) => {
            console.error('Upload frame failed:', error);
            message.error(t('Upload failed, please try again'));
            setUploadStatus(1);
          });
      }).catch((error) => {
        console.error('Get video frame failed:', error);
        message.error(t('Failed to extract video frame'));
        setUploadStatus(1);
      });

      // 进度条动画（10 秒）
      let animationStartTime: number | null = null;
      const duration = 10000;
      const updateProgress = (timestamp: number) => {
        if (!animationStartTime) animationStartTime = timestamp;
        const elapsed = timestamp - animationStartTime;

        const progress = Math.min(100, (elapsed / duration) * (elapsed / duration) * 100);
        setVideoProgress(progress);
        if (progress < 100) {
          requestAnimationFrame(updateProgress);
        } else {
          titleRef.current?.focus();
          titleRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          setUploadStatus(3);
          setVideoProgress(0);
          message.success(t('Upload successful'));
        }
      };

      requestAnimationFrame(updateProgress);
    }, 0);
  };
  const [uploadStatus, setUploadStatus] = useState<number>(1); // 1正常状态，2上传中  3上传成功
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleUpload(files[0]);
    }
  };
  const handleClear = () => {
    setUploadStatus(1);
    setImageData('');
    fileInputRef.current!.value = '';
  };

  const [isError, setIsError] = useState(false);
  const [title, setTitle] = useState('');
  const handleStart = () => {
    if (title == '') {
      setIsError(true);
      return;
    }
    if (!imageData) {
      message.error(t('Please upload the video first'));
      return;
    }
    if (videoProgress != 0) {
      message.error(t('Please wait for the video to upload'));
      return;
    }

    // Show skeleton immediately (for INP < 200ms)
    const startTime = performance.now();
    setLoading(true);
    setIsSearching(true);

    // Monitor skeleton render performance
    requestAnimationFrame(() => {
      const renderTime = performance.now() - startTime;
      console.log(`⏱️ Video skeleton rendered in ${renderTime.toFixed(2)}ms`);
    });

    ga_logEvent(EventName.reverse_result_generated);
    postVideoMonitorApi({
      username: title,
      file_url: imageData,
    }).then((res) => {
      if (res.code === 0) {
        callBackName(res.data, 'reverse-video-search', imageData);
      } else {
        message.error(res.message);
        setLoading(false);
        setIsSearching(false);
      }
    });
  };

  return (
    <div className={styles.container}>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) {
            handleUpload(files[0]);
          }
        }}
        accept="video/*"
      />
      <div className={styles.main}>
        <div className={styles.mainBast}>
          <div className={styles.mainBastIcon}>
            <div
              onClick={handleClick}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
              }}
            >
              <div>
                {uploadStatus == 1 && (
                  <div className={styles.mainBastIconInner}>
                    <div>
                      <div>
                        <span>+</span>
                        <span>{t('Upload video')}</span>
                      </div>
                      <div>{t('Support mp4, mov, avi, etc. Maximum support 100MB')}</div>
                    </div>
                    <div>
                      <Image
                        src={'https://res-front.pumpsoul.com/prod-erasa/image/content-monitoring/uploadVideo.webp'}
                        width={200}
                        height={154}
                        alt="upload"
                        fetchPriority="high"
                        priority
                      />
                    </div>
                  </div>
                )}
                {uploadStatus == 2 && (
                  <div className={styles.step2}>
                    <div>
                      <div>
                        <div>{t('Uploading...')}</div>
                        <div>{videoProgress.toFixed(0)}%</div>
                      </div>
                      <div>
                        <div style={{ width: `${videoProgress}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <Image
                        src={'https://res-front.pumpsoul.com/prod-erasa/image/content-monitoring/uploadVideo.webp'}
                        width={200}
                        height={154}
                        alt="upload"
                        fetchPriority="high"
                        priority
                      />
                    </div>
                  </div>
                )}
                {uploadStatus == 3 && (
                  <div className={styles.step3}>
                    <div className={styles.garbage} onClick={handleClear}>
                      <img
                        src="https://res-front.pumpsoul.com/prod-erasa/image/content-monitoring/garbage.svg"
                        alt="garbage"
                      />
                    </div>
                    <img
                      src={imageData}
                      alt="image"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div>
              <div>{t('Video title (required)')}</div>
              <div className={isError ? styles.errinput : styles.input}>
                <input
                  type="text"
                  placeholder={t('Please enter video title (required)')}
                  value={title}
                  ref={titleRef}
                  onFocus={() => {
                    setIsError(false);
                  }}
                  onChange={(e) => {
                    setTitle(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleStart();
                    }
                  }}
                  style={{ color: 'var(--primary-text)' }}
                />
              </div>
              <div>
                <Button onClick={handleStart} loading={loading}>
                  <Image
                    src="https://res-front.pumpsoul.com/prod-erasa/image/content-monitoring/btnRight.svg"
                    alt="erasa"
                    width={20}
                    height={20}
                    fetchPriority="high"
                    priority
                  ></Image>
                  <span>{t('Start detection')}</span>
                </Button>
              </div>
              <div>
                <Image
                  src="/images/common/green-right.svg"
                  alt="erasa"
                  width={15}
                  height={15}
                  fetchPriority="high"
                  priority
                ></Image>
                <span>{t('no signup or credit card needed')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
